import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ViewChild, ElementRef, forwardRef, inject, ChangeDetectorRef } from '@angular/core';
import { FormControl, ReactiveFormsModule, FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'ui-input',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiInputComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="relative">
      <input
        #innerInput
        [id]="id"
        [type]="type"
        [value]="modelValue ?? ''"
        [attr.placeholder]="placeholder"
        (input)="onInputEvent($event)"
        (change)="change.emit($event)"
        (focus)="focusEvent.emit($event)"
        (blur)="onBlurEvent($event)"
        (keydown)="keydown.emit($event)"
        (keyup)="keyup.emit($event)"
        [attr.name]="name"
        [required]="required"
        [disabled]="disabled || disabledByForm"
        [readonly]="readonly"
        [attr.autocomplete]="autocomplete"
        [class]="inputClass"
        [attr.aria-label]="ariaLabel"
        [attr.maxlength]="maxlength"
        [attr.minlength]="minlength"
        [attr.min]="min"
        [attr.max]="max"
        [attr.step]="step"
      />
      <ng-content select="[icon]"></ng-content>
    </div>
  `,
})
export class UiInputComponent implements ControlValueAccessor {
  @ViewChild('innerInput') innerInput?: ElementRef<HTMLInputElement>;

  @Input() id?: string;
  @Input() type: string = 'text';
  @Input() value?: string | number | null;
  @Input() placeholder?: string;
  @Input() name?: string;
  @Input() required = false;
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() autocomplete?: string;
  @Input() ariaLabel?: string;
  @Input() maxlength?: number;
  @Input() minlength?: number;
  @Input() min?: number | string;
  @Input() max?: number | string;
  @Input() step?: number | string;
  @Input() inputClass = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
  @Input() formControl?: FormControl;
  @Input() ngModel?: any;

  @Output() ngModelChange = new EventEmitter<any>();
  @Output() input = new EventEmitter<Event>();
  @Output() change = new EventEmitter<Event>();
  @Output() focusEvent = new EventEmitter<FocusEvent>();
  @Output() blurEvent = new EventEmitter<FocusEvent>();
  @Output() keydown = new EventEmitter<KeyboardEvent>();
  @Output() keyup = new EventEmitter<KeyboardEvent>();

  modelValue: any = '';
  disabledByForm = false;
  private cdr = inject(ChangeDetectorRef);
  private onChangeFn: (value: any) => void = () => {};
  private onTouchedFn: () => void = () => {};

  writeValue(value: any): void {
    this.modelValue = value ?? '';
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByForm = isDisabled;
  }

  onInputEvent(event: Event): void {
    const target = event.target as HTMLInputElement;
    const raw = target.value;
    // Para inputs numéricos, emite número (ou null se vazio/ inválido) ao invés de string,
    // evitando que o ControlValueAccessor propague strings para forms reativos tipados.
    const value = this.type === 'number'
      ? (raw === '' || Number.isNaN(target.valueAsNumber) ? null : target.valueAsNumber)
      : raw;
    this.modelValue = raw;
    this.ngModelChange.emit(value);
    this.onChangeFn(value);
    this.input.emit(event);
  }

  onBlurEvent(event: FocusEvent): void {
    this.onTouchedFn();
    this.blurEvent.emit(event);
  }

  focus(): void {
    this.innerInput?.nativeElement.focus();
  }
}
